import React from "react";
import { Box, Chip, Paper, Stack, TextField, Typography } from "@mui/material";
import {
  DASHBOARD_FONT,
  DASHBOARD_MUTED,
  DASHBOARD_NAVY,
  DASHBOARD_TEXT,
} from "../manage/dashboardUi";

export function SectionShell({
  id,
  title,
  subtitle,
  bgcolor,
  icon,
  children,
  extraToolbar,
  action,
  total,
  searchValue,
  onSearchChange,
}: {
  id: string;
  title: string;
  subtitle: string;
  bgcolor: string;
  icon: React.ReactNode;
  children: React.ReactNode;
  extraToolbar?: React.ReactNode;
  action?: React.ReactNode;
  total?: number;
  searchValue: string;
  onSearchChange: (value: string) => void;
}) {
  return (
    <Paper
      id={id}
      variant="outlined"
      sx={{
        fontFamily: DASHBOARD_FONT,
        borderRadius: 3,
        overflow: "hidden",
        mb: 2,
        scrollMarginTop: 88,
        width: "100%",
        maxWidth: "100%",
        minWidth: 0,
        borderColor: "#d7dee6",
        "& .rdt_Table, & .rdt_TableCol, & .rdt_TableCell, & .rdt_Pagination": {
          fontFamily: `${DASHBOARD_FONT} !important`,
          color: DASHBOARD_TEXT,
        },
        "& .rdt_TableCol": {
          fontSize: "0.78rem !important",
          fontWeight: "700 !important",
          color: `${DASHBOARD_TEXT} !important`,
          letterSpacing: "0.01em",
          flexGrow: "0 !important",
          flexShrink: "0 !important",
        },
        "& .rdt_TableCell": {
          fontSize: "0.82rem !important",
          fontWeight: 500,
          color: DASHBOARD_TEXT,
          flexGrow: "0 !important",
          flexShrink: "0 !important",
        },
        "& .rdt_Pagination": {
          fontSize: "0.78rem !important",
          color: `${DASHBOARD_MUTED} !important`,
        },
        "& .rdt_TableHeadRow, & .rdt_TableRow": {
          minWidth: "max-content",
          width: "max-content !important",
        },
        "& .c-overflow": {
          display: "block",
          width: "100%",
        },
        "& [data-testid='table-wrapper'], & .rdt_TableWrapper": {
          overflowX: "visible !important",
        },
        "& .rdt_TableBody": {
          height: "380px !important",
          maxHeight: "380px !important",
          overflowY: "auto !important",
          overflowX: "hidden !important",
          marginRight: "0 !important",
        },
      }}
    >
      <Box
        sx={{
          p: 2,
          bgcolor,
          display: "flex",
          gap: 2,
          flexDirection: { xs: "column", md: "row" },
          alignItems: { md: "center" },
        }}
      >
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Stack direction="row" spacing={1} alignItems="center">
            {icon}
            <Typography
              variant="h6"
              sx={{
                fontFamily: DASHBOARD_FONT,
                fontWeight: 750,
                fontSize: "1.1rem",
                color: DASHBOARD_TEXT,
                letterSpacing: "-0.01em",
              }}
            >
              {title}
            </Typography>
            <Chip
              size="small"
              label={total ?? 0}
              sx={{
                fontFamily: DASHBOARD_FONT,
                fontWeight: 700,
                bgcolor: "#ffffff",
                color: DASHBOARD_NAVY,
                border: "1px solid #c5d4e0",
              }}
            />
          </Stack>
          <Typography
            variant="body2"
            sx={{
              fontFamily: DASHBOARD_FONT,
              color: DASHBOARD_MUTED,
              mt: 0.25,
              fontSize: "0.875rem",
            }}
          >
            {subtitle}
          </Typography>
        </Box>
        <Stack
          direction={{ xs: "column", sm: "row" }}
          spacing={1}
          alignItems="center"
          sx={{ flexShrink: 0 }}
        >
          {extraToolbar}
          <TextField
            size="small"
            placeholder={`Search ${title}...`}
            value={searchValue}
            onChange={(e) => onSearchChange(e.target.value)}
            sx={{
              minWidth: 220,
              bgcolor: "white",
              "& .MuiInputBase-root": {
                fontFamily: DASHBOARD_FONT,
                fontSize: "0.875rem",
                color: DASHBOARD_TEXT,
              },
              "& .MuiInputBase-input::placeholder": {
                color: DASHBOARD_MUTED,
                opacity: 1,
              },
            }}
          />
          {action}
        </Stack>
      </Box>
      <Box sx={{ width: "100%", minWidth: 0, overflow: "hidden" }}>
        {children}
      </Box>
    </Paper>
  );
}
